import { useCallback, useEffect, useMemo } from 'react';

import { useShowDonationItem, useShowStoreCause } from '@givebacks/api/store';
import { colorTo } from '@givebacks/color-bot';
import { Native } from '@givebacks/utils';
import { useShowCauseW_Selector } from 'core/Api';
import { useColorBotQuery, usePlatformService } from 'core/Hooks';
import { truncateUrl } from 'core/Utils';
import { useSetAtom } from 'jotai';
import { storeCauseIdAtom } from 'store';
import { useSelectStoreItemProvider } from 'store/Components';
import {
  Anchor,
  AspectRatio,
  Box,
  ButtonMantine,
  Container,
  Flex,
  Group,
  ImageMantine,
  Stack,
  Text,
  Title,
} from 'ui';

import { useCausePageContext } from '../../CauseContext';
import { CausePageHeaderActions } from '../CausePageHeaderActions/CausePageHeaderActions';
import { FollowButton } from '../FollowButton/FollowButton';
import classes from './CausePageHeader.module.css';

const CauseLogo = ({ url, className }: { url?: string; className?: string }) => {
  return (
    <AspectRatio
      ratio={1}
      miw={{
        base: 75,
        sm: 131,
      }}
      w={{
        base: 75,
        sm: 131,
      }}
      className={className}
    >
      <ImageMantine src={url} radius="12" />
    </AspectRatio>
  );
};

const CausePageHeader = () => {
  const Platform = usePlatformService();
  const { cause, showStoreTab } = useCausePageContext();

  const { data: causeWithStripe } = useShowCauseW_Selector({
    uuid: cause?.uuid,
    include: 'stripe_connected',
  });

  const setStoreCauseId = useSetAtom(storeCauseIdAtom);
  const { data: storeCause } = useShowStoreCause({
    causeId: cause?.uuid,
    queryOptions: {
      options: {
        include: 'store_active',
      },
    },
  });
  const { openModalById, setMeta } = useSelectStoreItemProvider({});
  const { data: donationItem } = useShowDonationItem({ storeId: cause?.uuid });

  const { data: colorBotData } = useColorBotQuery({
    imageSrc: cause.logo?.url,
  });

  const websiteLink = useMemo(() => {
    const linkObj = cause.links?.find((link) => link.type === 'website');

    if (!linkObj) return null;

    const handleClick = () => {
      if (Platform.isNative) {
        Native.postURL(linkObj.url);
      } else {
        window.open(linkObj.url, '_blank', '');
      }
    };

    return (
      <Anchor component="button" onClick={handleClick} td="underline">
        <Text className={classes.CausePageHeader__websiteLink}>{truncateUrl(linkObj.url)}</Text>
      </Anchor>
    );
  }, [cause.links]);

  const canDonate = useMemo(() => {
    if (!cause?.release.donations.beta) return false;
    if (!storeCause?.donations_enabled) return false;

    return true;
  }, [cause?.release.donations.beta, storeCause?.donations_enabled]);

  const canFollow = useMemo(() => {
    return causeWithStripe?.stripe_connected || cause?.givinga_charity_id;
  }, [cause?.givinga_charity_id, causeWithStripe?.stripe_connected]);

  const handleStartDonationClick = useCallback(() => {
    setMeta((prev) => ({
      ...prev,
      storeId: cause?.uuid,
      storeItemId: donationItem?.uuid,
      cartCredit: undefined,
    }));
    openModalById('StoreItemOptions');
  }, [cause?.uuid, donationItem?.uuid, openModalById, setMeta]);

  useEffect(() => {
    setStoreCauseId(cause.uuid);
  }, [cause]);

  return (
    <Box
      className={classes.CausePageHeader}
      mod={{
        show: !!colorBotData,
      }}
      style={{
        '--cause-page-header--primary': `${colorTo(colorBotData?.colorShades?.[5] ?? '').hex}`,
        '--cause-page-header--darkened': `${colorTo(colorBotData?.darkColor ?? '').hex}`,
      }}
    >
      <Container size="sm" px={{ base: '0px', sm: 'xs' }}>
        <Box className={classes.CausePageHeader__background} />
        <Flex id="cause-page-header" className={classes.CausePageHeader__content}>
          <Box className={classes.CausePageHeader__logo}>
            <CauseLogo url={cause.logo?.url} />
          </Box>

          <Box className={classes.CausePageHeader__info}>
            <Stack gap="4">
              <Title order={2}>{cause.name}</Title>
              <Box>{websiteLink}</Box>
            </Stack>
          </Box>

          <Group className={classes.CausePageHeader__actions}>
            {canFollow && (
              <FollowButton
                color={colorBotData ? 'white' : 'black'}
                className={classes.CausePageHeader__action}
              />
            )}
            {canDonate && (
              <ButtonMantine
                variant="subtle"
                color={colorBotData ? 'white' : 'black'}
                className={classes.CausePageHeader__action}
                onClick={handleStartDonationClick}
              >
                Donate
              </ButtonMantine>
            )}
            <Box className={classes.CausePageHeader__kebab}>
              <CausePageHeaderActions
                color={colorBotData ? 'white' : 'black'}
                // showStoreTab is true if there are store items, payments are enabled, and store is active
                hasStore={showStoreTab}
              />
            </Box>
          </Group>
        </Flex>
      </Container>
    </Box>
  );
};

export { CausePageHeader };
